import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography, TextField, CircularProgress, Paper } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';

const WrappedHeader = (props) => (
  <div
    style={{
      whiteSpace: 'normal',
      lineHeight: 'normal',
      textAlign: 'center',
      padding: '4px 0',
    }}
  >
    {props.colDef.headerName}
  </div>
);

const NutsTable = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterText, setFilterText] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch('/NUTS_ISO4032.json');
        if (!res.ok) throw new Error('Failed to load nuts data');
        const data = await res.json();
        if (!cancelled) setRows(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!cancelled) {
          setError('Could not load NUTS_ISO4032.json');
          setRows([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const filteredRows = useMemo(() => {
    if (!filterText.trim()) return rows;
    const q = filterText.toLowerCase();
    return rows.filter(
      (r) =>
        String(r.name || '').toLowerCase().includes(q) ||
        String(r.d || '').includes(q)
    );
  }, [rows, filterText]);

  const columns = useMemo(
    () => [
      { field: 'id', headerName: 'ID', hide: true },
      {
        field: 'name',
        headerName: 'Size',
        width: 80,
        renderHeader: (p) => <WrappedHeader {...p} />,
      },
      {
        field: 'd',
        headerName: 'd\n(mm)',
        type: 'number',
        width: 70,
        renderHeader: (p) => <WrappedHeader {...p} />,
      },
      {
        field: 's',
        headerName: 's\nacross flats\n(mm)',
        type: 'number',
        width: 100,
        renderHeader: (p) => <WrappedHeader {...p} />,
      },
      {
        field: 'e_min',
        headerName: 'e min\nacross corners\n(mm)',
        type: 'number',
        width: 110,
        renderHeader: (p) => <WrappedHeader {...p} />,
      },
      {
        field: 'm_max',
        headerName: 'm max\nnut height\n(mm)',
        type: 'number',
        width: 100,
        renderHeader: (p) => <WrappedHeader {...p} />,
      },
    ],
    []
  );

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" paragraph sx={{ maxWidth: 800 }}>
        ISO 4032:2012 — hexagon nuts, style 1. M10/M12 width across flats (s) were reduced vs older DIN 934; confirm with
        supplier for mixed stock.
      </Typography>
      <Box mb={2}>
        <TextField
          label="Filter by size"
          size="small"
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
        />
      </Box>
      {loading ? (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Typography color="error">{error}</Typography>
      ) : (
        <Paper variant="outlined" sx={{ width: '100%', maxWidth: 640 }}>
          <DataGrid
            autoHeight
            rows={filteredRows}
            columns={columns}
            getRowId={(r) => r.id}
            checkboxSelection={false}
            disableRowSelectionOnClick
            density="compact"
            initialState={{
              columns: { columnVisibilityModel: { id: false } },
            }}
            sx={{
              border: 0,
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: 'action.hover',
                lineHeight: 'normal',
              },
            }}
          />
        </Paper>
      )}
    </Box>
  );
};

export default NutsTable;
